import React, {createContext, FC, useContext, useEffect, useRef} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import LaravelEcho from '../../js/echo';
import {Notification as NotificationType} from '../types/Notification';
import assetUrl from '../utils/assetUrl';
import NotificationController from 'puddleglum/api/NotificationController';

type NotificationContextValue = {
	registerCallback: (channel: string, callback: (notification: NotificationType) => void) => void;
	notifications?: NotificationType[];
	refetch: () => void;
};

const NotificationContext = createContext({} as NotificationContextValue);

export const useNotifications = () => {
	const context = useContext(NotificationContext);
	if (context === undefined) {
		throw new Error('useNotifications must be used within a NotificationProvider');
	}
	return context;
};

export const NotificationProvider: FC<{children: React.ReactNode}> = ({children}) => {
	// Captured once. Reading document.title on every render would re-capture a title
	// this effect had already prefixed, compounding the prefix on each update.
	const initialTitle = useRef(document.title).current;
	const registeredChannels = useRef<string[]>([]);
	const queryClient = useQueryClient();
	const [isPushEnabled, setIsPushEnabled] = React.useState<boolean>(false);
	const {data: notifications, refetch} = useQuery(
		['notifications'],
		async () => {
			const response = await NotificationController.getNotifications();
			return response.data;
		},
		{
			// Backstop for missed broadcasts; pauses while the tab is unfocused,
			// and refetchOnWindowFocus catches us up on return.
			refetchInterval: 60000,
		},
	);
	const unreadNotifications = notifications?.filter((notification) => !notification.read_at);
	const unreadCount = unreadNotifications?.length ?? 0;

	const registerCallback = (
		channel: string,
		callback: (notification: NotificationType) => void,
	) => {
		// Prevent registering the same channel multiple times
		if (!registeredChannels.current.includes(channel)) {
			registeredChannels.current.push(channel);
			LaravelEcho.private(channel).notification((notification: NotificationType) => {
				queryClient.setQueryData<NotificationType[]>(['notifications'], (current = []) => [
					notification,
					...current.filter((existing) => existing.id !== notification.id),
				]);

				if (isPushEnabled) {
					const browserNotification = new Notification(notification.title, {
						body: notification.body,
						icon: assetUrl('/images/logo.png'),
						tag: notification.id,
					});
					browserNotification.onclick = () => {
						browserNotification.close();
						window.parent.focus();
					};
				}

				callback?.(notification);
				refetch();
			});
		}
	};

	const enablePushNotifications = async () => {
		try {
			const permission = await Notification.requestPermission();
			setIsPushEnabled(permission === 'granted');
		} catch (error) {
			console.error('Error enabling push notifications:', error);
		}
	};

	useEffect(() => {
		if (unreadCount === 0) {
			document.title = initialTitle;
		} else {
			const noun = unreadCount === 1 ? 'New Notification' : 'New Notifications';
			document.title = `(${unreadCount}) ${noun} | ${initialTitle}`;
		}

		return () => {
			document.title = initialTitle;
		};
	}, [unreadCount, initialTitle]);

	useEffect(() => {
		enablePushNotifications();
	}, []);

	return (
		<NotificationContext.Provider
			value={{
				registerCallback,
				notifications,
				refetch,
			}}
		>
			{children}
		</NotificationContext.Provider>
	);
};
